import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import useDataFetch from "../hooks/useDataFetch";
import { MobileResponsive } from "./MobileResponsive";
import { Map } from "@vis.gl/react-google-maps";
import { ScatterplotLayer } from "deck.gl";
import { getH3HexagonRadius, getHexCenter } from "../utils/mapUtils";
import { MapMarker } from "../components/MapMarker";
import { DeckGLOverlay } from "../components/MapOverlay";
import { InfoWindowPopup } from "../components/InfoWindow";
import DynamicMetaTags from "../components/DynamicMetaTags";

const ActiveHubs = () => {
  const { data: rawData, isLoading } = useDataFetch("/admin/hotspots");

  // Normalize: API may return array directly or wrapped in a property
  const hotspots = Array.isArray(rawData)
    ? rawData
    : rawData?.hotspots || rawData?.hotspotMarkers || [];

  const [mapCenter, setMapCenter] = useState({ lat: 54.5, lng: -3.0 });
  const [selectedMarker, setSelectedMarker] = useState({ selectedMarker: null, entityData: [] });
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  // Center map on the hub with the most orders
  useEffect(() => {
    if (hotspots.length === 0) return;
    const top = hotspots.reduce((max, h) => {
      const count = (h.Pending || 0) + (h.Delivering || 0) + (h.Done || 0);
      const maxCount = (max.Pending || 0) + (max.Delivering || 0) + (max.Done || 0);
      return count > maxCount ? h : max;
    }, hotspots[0]);
    if (top?.hexString) {
      const [lat, lng] = getHexCenter(top.hexString);
      setMapCenter({ lat: lng, lng: lat });
    }
  }, [hotspots.length]);

  // Normalise outlets to objects InfoWindow can render
  const resolveOutlets = (hotspot) =>
    (hotspot?.outlets || []).map((o) =>
      typeof o === "string" ? { outlet_name: o } : o
    );

  const handleMarkerHover = (id, marker) => {
    const hub = hotspots.find((h) => h?._id === id);
    setSelectedMarker({
      selectedMarker: marker,
      entityData: resolveOutlets(hub),
    });
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({ selectedMarker: null, entityData: null });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({ selectedMarker: null, entityData: [] });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (selectedMarker.entityData?.length > 0 && selectedMarker.selectedMarker) {
      setInfoWindowShown(true);
    }
  }, [selectedMarker]);

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Active Hub Monitoring & Status Updates"
        pageDescription="Get real-time insights with Beep's dashboard! Monitor active drivers, food outlets, hubs, and detailed analytics on orders. Track Pending, Delivering, and Done orders seamlessly across outlets and hubs."
        pageKeywords="Beep, logistics dashboard, real-time analytics, active drivers, active food outlets, active hubs, order tracking, pending orders, delivering orders, completed orders, logistics insights"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between w-full">
            <Link to="/" className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img src="/assets/left-white-arrow.svg" alt="" className="w-[6px]" />
              </div>
              <span className="font-semibold text-base">Active Hubspot</span>
            </Link>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 w-full h-[80dvh]">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          ) : (
            <>
              {/* Map */}
              <div className="w-full rounded-lg flex flex-col items-center justify-between gap-4 py-8">
                <div className="flex flex-col justify-center items-center gap-5 w-full 2xl:h-[45vh] xl:h-[37vh] h-[37vh] rounded-lg overflow-hidden shadow-default">
                  <Map
                    defaultCenter={mapCenter}
                    defaultZoom={5}
                    mapId="terrain"
                    gestureHandling={"greedy"}
                    disableDefaultUI
                    styles={"terrain"}
                    onClick={onMapClick}
                    fullscreenControl={true}
                    mapTypeId={"terrain"}
                    isFractionalZoomEnabled={true}
                    mapTypeControlOptions
                    zoomControl
                    scaleControl
                    streetViewControl
                    mapTypeControl
                    cameraControl
                    rotateControl
                  >
                    {hotspots.map((hotspot) => {
                      const [lat, lng] = getHexCenter(hotspot?.hexString);
                      const radius = getH3HexagonRadius(hotspot?.hexString);
                      const layers = [
                        new ScatterplotLayer({
                          id: `deckgl-circle-${hotspot?._id || hotspot?.hexString}`,
                          data: [{ position: [lat, lng] }],
                          getPosition: (d) => d.position,
                          getFillColor: [179, 20, 18, 150],
                          getRadius: radius * 1000,
                        }),
                      ];
                      return (
                        <React.Fragment key={hotspot?._id || hotspot?.hexString}>
                          <MapMarker
                            data={[{ lat: lng, lng: lat, hotspot }]}
                            entityType="hubspots"
                            onMouseEnter={handleMarkerHover}
                          />
                          <DeckGLOverlay layers={layers} />
                        </React.Fragment>
                      );
                    })}
                    <InfoWindowPopup
                      infoWindowShown={infoWindowShown}
                      selectedMarker={selectedMarker}
                      handleCloseInfoWindow={handleCloseInfoWindow}
                      type="hubspots"
                    />
                  </Map>
                </div>
              </div>

              {/* Table */}
              <div className="w-full rounded-lg flex flex-col gap-4 py-4">
                <div className="border-2 border-t-4 border-quaternary bg-[#F7F9F8] text-[#413F3F] overflow-x-auto relative rounded-xl">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <thead>
                      <tr>
                        {["Sr No.", "Hubspot ID", "Pending", "Delivering", "Done", "Total"].map((h) => (
                          <th key={h} className="text-start text-sm font-bold p-3 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    {hotspots.length > 0 ? (
                      <tbody className="divide-y divide-[#8D8D8D]">
                        {hotspots.map((item, index) => (
                          <tr key={item?._id || item?.hexString || index} className={index % 2 === 0 ? "bg-gray-100" : ""}>
                            <td className="p-3 text-sm font-medium">{index + 1}</td>
                            <td className="p-3 text-sm font-medium truncate max-w-xs">{item?.hexString}</td>
                            <td className="p-3 text-sm font-medium text-amber-600">{item?.Pending ?? 0}</td>
                            <td className="p-3 text-sm font-medium text-blue-600">{item?.Delivering ?? 0}</td>
                            <td className="p-3 text-sm font-medium text-green-600">{item?.Done ?? 0}</td>
                            <td className="p-3 text-sm font-semibold">
                              {(item?.Pending || 0) + (item?.Delivering || 0) + (item?.Done || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ) : (
                      <tbody>
                        <tr>
                          <td colSpan={6} className="p-4 italic text-sm font-medium text-center text-gray-500">
                            No active hubs found.
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ActiveHubs;
